/**
 * PartyTransport — Production transport using WebSocket to connect to
 * the Cloudflare edge Worker with PartyServer rooms.
 *
 * Implements the GameTransport interface so Game code depends on the
 * interface, not the concrete backend.
 */

import type { GameTransport, ServerMessageHandler, TransportClientMessage, TransportServerMessage } from './Transport.js';
import { PROTOCOL_VERSION } from '@shared/protocol/version.js';

export interface PartyTransportOptions {
  /** Edge Worker host (e.g. 'voxel-game-edge.jilrot.workers.dev'). */
  host: string;
  /** Existing edge room ID. When set, connect to this room instead of creating one. */
  roomId?: string;
  /** Player display name. */
  playerName?: string;
  /** Cosmetic skin id advertised to the room. */
  playerSkinId?: string;
  /** Game mode for room creation. */
  mode?: string;
  /** Map ID for room creation. */
  mapId?: string;
  /** Optional Cloudflare Durable Object location hint for ad-hoc rooms. */
  locationHint?: string;
}

export class PartyTransport implements GameTransport {
  private socket: WebSocket | null = null;
  private messageHandlers: Set<ServerMessageHandler> = new Set();
  private pendingMessages: TransportServerMessage[] = [];
  private options: PartyTransportOptions;
  private token: string | null = null;
  private roomId: string | null = null;
  private _sessionId: string | null = null;
  private _isConnected = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionalClose = false;
  private helloQueued: Record<string, unknown> | undefined = undefined;
  private readonly RECONNECT_DELAY_MS = 2000;

  constructor(options: PartyTransportOptions) {
    this.options = options;
    this.roomId = options.roomId ?? null;
  }

  get sessionId(): string | null {
    return this._sessionId;
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  async connect(): Promise<void> {
    await this.ensureToken();
    await this.createRoom();
    // Connect WebSocket (hello deferred until joinRoom so handler is registered)
    await this.openSocket();
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async joinRoom(_roomName: string, options?: Record<string, unknown>): Promise<void> {
    // Send hello now that Game.ts has registered its onMessage handler.
    // Save options for reconnect scenarios.
    this.helloQueued = options;
    this.sendHello(options);
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async leaveRoom(): Promise<void> {
    this.detachSocketListeners();
    this.intentionalClose = true;
    if (this.socket !== null) {
      this.socket.close(1000, 'left room');
    }
    this._sessionId = null;
    this._isConnected = false;
    this.roomId = null;
    this.pendingMessages = [];
  }

  send(message: TransportClientMessage): void {
    if (this.socket !== null && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  onMessage(handler: ServerMessageHandler): () => void {
    this.messageHandlers.add(handler);
    for (const msg of this.pendingMessages) {
      handler(msg);
    }
    this.pendingMessages = [];
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async disconnect(): Promise<void> {
    this.detachSocketListeners();
    this.intentionalClose = true;
    this.cancelReconnect();
    this.messageHandlers.clear();
    this.pendingMessages = [];
    if (this.socket !== null) {
      this.socket.close(1000, 'transport disconnect');
      this.socket = null;
    }
    this._sessionId = null;
    this._isConnected = false;
  }

  // ── Private helpers ──

  private sendHello(options?: Record<string, unknown>): void {
    const playerName = (options?.['name'] as string | undefined) ??
      this.options.playerName ??
      `Player_${Math.random().toString(36).slice(2, 8)}`;

    this.send({
      type: 'hello',
      clientSeq: 0,
      payload: {
        protocolVersion: PROTOCOL_VERSION,
        playerName,
        ...(this.options.playerSkinId !== undefined ? { skinId: this.options.playerSkinId } : {}),
        sessionToken: this.token,
        supportedCodecs: ['json'],
      },
    });
  }

  private async ensureToken(): Promise<void> {
    if (this.token !== null) return;

    const baseUrl = this.httpOrigin();
    const response = await fetch(`${baseUrl}/session/guest`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error(`Failed to get guest token: ${String(response.status)}`);
    }
    const body = (await response.json()) as { token: string };
    this.token = body.token;
  }

  private async createRoom(): Promise<void> {
    if (this.roomId !== null) return;

    const baseUrl = this.httpOrigin();
    const response = await fetch(`${baseUrl}/rooms/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode: this.options.mode ?? 'freePlay',
        mapId: this.options.mapId ?? 'default',
        ...(this.options.locationHint !== undefined ? { locationHint: this.options.locationHint } : {}),
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to create room: ${String(response.status)}`);
    }
    const body = (await response.json()) as { roomId: string; wsUrl: string };
    this.roomId = body.roomId;
  }

  private async openSocket(): Promise<void> {
    this.closeCurrentSocket();

    const roomId = this.roomId;
    if (roomId === null) {
      throw new Error('No room ID available');
    }

    const host = this.normalizedHost();
    const protocol = host.startsWith('localhost') || host.startsWith('127.') ? 'ws' : 'wss';
    const playerName = this.options.playerName ??
      `Player_${Math.random().toString(36).slice(2, 8)}`;
    const query = new URLSearchParams({
      name: playerName,
      token: this.token ?? '',
    });
    if (this.options.playerSkinId !== undefined) {
      query.set('skinId', this.options.playerSkinId);
    }
    const wsUrl = `${protocol}://${host}/rooms/${encodeURIComponent(roomId)}/ws?${query.toString()}`;

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl);

      const onOpen = (): void => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        this.socket = ws;
        this._isConnected = true;

        ws.addEventListener('message', this.onSocketMessage);
        ws.addEventListener('close', this.onSocketClose);

        // hello is deferred — sent via joinRoom after Game registers handlers
        resolve();
      };

      const onError = (): void => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        reject(new Error('WebSocket connection failed'));
      };

      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
    });
  }

  /**
   * Detach listeners and close the current socket without triggering
   * the reconnect/handler-corruption paths in onSocketClose.
   */
  private closeCurrentSocket(): void {
    if (this.socket === null) return;
    this.detachSocketListeners();
    this.intentionalClose = true;
    this.socket.close(1000, 'reconnecting');
  }

  private detachSocketListeners(): void {
    if (this.socket === null) return;
    this.socket.removeEventListener('message', this.onSocketMessage);
    this.socket.removeEventListener('close', this.onSocketClose);
  }

  private onSocketMessage = (event: MessageEvent): void => {
    try {
      const raw = JSON.parse(event.data as string) as unknown;
      if (typeof raw !== 'object' || raw === null) return;

      const msg = raw as Record<string, unknown>;

      // Capture sessionId from welcome message
      if (msg['type'] === 'welcome' && typeof msg['payload'] === 'object' && msg['payload'] !== null) {
        const payload = msg['payload'] as Record<string, unknown>;
        if (typeof payload['sessionId'] === 'string') {
          this._sessionId = payload['sessionId'];
        }
      }

      const serverMessage = msg as { type: string; payload?: unknown; serverTick?: number };
      if (this.messageHandlers.size === 0) {
        this.pendingMessages.push(serverMessage);
        return;
      }

      for (const handler of this.messageHandlers) {
        handler(serverMessage);
      }
    } catch {
      // Ignore malformed messages
    }
  };

  private onSocketClose = (): void => {
    this._isConnected = false;
    this._sessionId = null;
    this.socket = null;

    // Only reconnect for unexpected disconnects
    if (!this.intentionalClose && this.messageHandlers.size > 0) {
      this.scheduleReconnect();
    }
    this.intentionalClose = false;
  };

  private scheduleReconnect(): void {
    this.cancelReconnect();
    this.reconnectTimer = setTimeout(() => {
      void this.tryReconnect();
    }, this.RECONNECT_DELAY_MS);
  }

  private async tryReconnect(): Promise<void> {
    try {
      await this.openSocket();
      this.sendHello(this.helloQueued);
    } catch {
      this.scheduleReconnect();
    }
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private normalizedHost(): string {
    return this.options.host
      .replace(/^https?:\/\//, '')
      .replace(/^wss?:\/\//, '')
      .replace(/\/$/, '');
  }

  private httpOrigin(): string {
    const host = this.normalizedHost();
    const protocol = host.startsWith('localhost') || host.startsWith('127.') ? 'http' : 'https';
    return `${protocol}://${host}`;
  }
}
